import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { baseApolloClient } from 'src/services/apollo/client'
import {
  GetTaskByIdDocument,
  ListMyTasksDocument,
  ListMyTasksTypedDocument,
  ListUserTaskSummariesDocument,
} from 'src/services/apollo/generated/graphql'
import { orpc } from './contract'

export type ModelExperimentSessionStatus =
  | 'pending'
  | 'selecting'
  | 'resolved'
  | 'expired'

export type ModelExperimentChoice =
  | 'picked_left'
  | 'picked_right'
  | 'tie'
  | 'skip'

const POLL_INTERVAL_MS = 5000

type SessionCacheShape = {
  id: string
  status: ModelExperimentSessionStatus
  leftMedias: { mediaId: string; mediaUrl: string | null }[] | null
  rightMedias: { mediaId: string; mediaUrl: string | null }[] | null
  choice: ModelExperimentChoice | null
}

/**
 * Build a closure that flips the cached session to `resolved` with the user's
 * choice. Extracted to a pure helper so it's testable without rendering a hook.
 */
export function buildPreferenceSuccessCacheUpdate(args: {
  queryClient: QueryClient
  taskId: string
  choice: ModelExperimentChoice
}) {
  const { queryClient, taskId, choice } = args
  return () => {
    const queryKey = orpc.modelExperimentSession.getExpSession.queryKey({
      input: { taskId },
    })
    queryClient.setQueryData<SessionCacheShape | undefined>(queryKey, prev =>
      prev ? { ...prev, status: 'resolved', choice } : prev,
    )
  }
}

/**
 * Read the session entry for `taskId` straight out of the react-query cache.
 * Returns `undefined` when no `useModelExperimentSession` is mounted for the
 * task (or the query hasn't resolved yet) — callers should treat that as
 * "no signal" rather than "not selecting".
 *
 * Used by passive consumers (e.g. card click handlers) that want to peek at
 * the session without mounting their own query and tripping the polling
 * loop.
 */
export function readExperimentSessionFromCache(args: {
  queryClient: QueryClient
  taskId: string
}): SessionCacheShape | undefined {
  const { queryClient, taskId } = args
  const queryKey = orpc.modelExperimentSession.getExpSession.queryKey({
    input: { taskId },
  })
  return queryClient.getQueryData<SessionCacheShape>(queryKey)
}

export function useModelExperimentSession(args: {
  taskId: string | undefined
  enabled?: boolean
}) {
  const { taskId, enabled } = args
  return useQuery({
    ...orpc.modelExperimentSession.getExpSession.queryOptions({
      input: { taskId: taskId ?? '' },
    }),
    enabled: !!taskId && (enabled ?? true),
    refetchInterval: query => {
      const status = (query.state.data as SessionCacheShape | undefined)?.status
      return status === 'pending' || status === 'selecting'
        ? POLL_INTERVAL_MS
        : false
    },
    retry: (count, err) => {
      const status = (err as { status?: number } | undefined)?.status
      if (status === 404) return false
      return count < 1
    },
  })
}

export function useSubmitModelExperimentPreference(args: { taskId: string }) {
  const { taskId } = args
  const queryClient = useQueryClient()

  return useMutation({
    ...orpc.modelExperimentSession.submitPreference.mutationOptions(),
    onSuccess: async (_data, variables) => {
      const update = buildPreferenceSuccessCacheUpdate({
        queryClient,
        taskId,
        choice: (variables as { choice: ModelExperimentChoice }).choice,
      })
      update()
      // Backend's commitChoice already called completeTask(root, …); refetch
      // the task lists / task-by-id so Apollo cache picks up the new
      // outputs.mediaId. Document refs (not string names) so that renaming
      // any of these queries triggers a typecheck error rather than a
      // silent post-submit cache miss.
      await baseApolloClient.refetchQueries({
        include: [
          ListMyTasksDocument,
          ListMyTasksTypedDocument,
          ListUserTaskSummariesDocument,
          GetTaskByIdDocument,
        ],
      })
    },
  })
}
